import React, { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import propertiesService from '../services/propertiesService';
import { notification, Alert } from 'antd';
import userRoleUtils from '../utils/userRoleUtils';
import { X, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import eventBus from '../utils/eventBus';
import cartService from '../services/cartService';
 
const DateTimeModal = ({
  isOpen,
  onClose,
  propertyId,
  initialData,
  onSave,
  orderId = null,
  isSaveHidden = false
}) => {
  const [formData, setFormData] = useState({
    arrival_times: ['08', '00'],
    completion_times: ['09', '30'],
    date: ''
  });
  
  const [validationErrors, setValidationErrors] = useState({
    date: null,
    time: null
  });
  
  const [isLoading, setIsLoading] = useState(false);

  // Get the minimum allowed date based on user role
  const getMinimumDate = () => {
    return userRoleUtils.getMinimumOrderDate();
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // ✅ SIMPLIFIED: Initialize form data
  useEffect(() => {
    if (isOpen) {
      let defaultDate;
      
      if (userRoleUtils.isAdminOrImpersonating()) {
        defaultDate = getTodayDate();
      } else {
        defaultDate = userRoleUtils.getMinimumOrderDate();
      }
      
      const defaultData = {
        arrival_times: ['08', '00'],
        completion_times: ['09', '30'],
        date: defaultDate
      };
      
      if (orderId) {
        defaultData.orderId = orderId;
      }
      
      // ✅ KEY FIX: Use property default times if available
      if (initialData) {
        if (initialData.arrival_time) {
          const [hours, minutes] = initialData.arrival_time.split(':');
          defaultData.arrival_times = [hours, minutes];
        }
        
        if (initialData.completion_time) {
          const [hours, minutes] = initialData.completion_time.split(':');
          defaultData.completion_times = [hours, minutes];
        }
        
        if (initialData.order_date) {
          if (userRoleUtils.isAdminOrImpersonating()) {
            defaultData.date = initialData.order_date;
          } else {
            defaultData.date = userRoleUtils.getEffectiveOrderDate(initialData.order_date);
          }
        }
      }

      setFormData(defaultData);
      validateFormData(defaultData);
    }
  }, [initialData, isOpen]);

  // Available options for hours and minutes
  const hoursOptions = Array.from({ length: 13 }, (_, i) => i + 8).map(h => h.toString().padStart(2, '0'));
  const minutesOptions = ['00', '15', '30', '45'];

  // Convert hours and minutes to decimal time value
  const getTimeValue = (hours, minutes) => {
    return parseFloat(hours) + parseFloat(minutes) / 60;
  };

  const calculateCompletionTime = (arrivalHour, arrivalMinutes) => {
    const arrivalValue = getTimeValue(arrivalHour, arrivalMinutes);
    let completionValue = arrivalValue + 1.5;
    
    const completionHour = Math.floor(completionValue);
    let completionMinutes = Math.round((completionValue - completionHour) * 60);
    
    const roundedMinutes = minutesOptions.reduce((closest, current) => {
      return Math.abs(current - completionMinutes) < Math.abs(closest - completionMinutes) ? current : closest;
    }, minutesOptions[0]);
    
    return [completionHour.toString().padStart(2, '0'), roundedMinutes];
  };

  const validateFormData = (data) => {
    const errors = { date: null, time: null };
    const isAdmin = userRoleUtils.isAdminOrImpersonating();

    if (!isAdmin) {
      const minDate = getMinimumDate();
      if (new Date(data.date) < new Date(minDate)) {
        errors.date = `Date must be on or after ${new Date(minDate).toLocaleDateString('en-GB')}`;
      }
    }

    const arrival = getTimeValue(data.arrival_times[0], data.arrival_times[1]);
    const completion = getTimeValue(data.completion_times[0], data.completion_times[1]);

    if (completion - arrival < 1.5) {
      errors.time = 'Time slot must be at least 1.5 hours';
    }

    setValidationErrors(errors);
    return !errors.date && !errors.time;
  };

  // Handle changes to arrival time
  const handleArrivalTimeChange = (index, value) => {
    const newArrivalTimes = [...formData.arrival_times];
    newArrivalTimes[index] = value;
    
    const newCompletionTimes = calculateCompletionTime(newArrivalTimes[0], newArrivalTimes[1]);
    
    const updatedFormData = {
      ...formData,
      arrival_times: newArrivalTimes,
      completion_times: newCompletionTimes
    };
    
    setFormData(updatedFormData);
    validateFormData(updatedFormData);
  };

  // Handle changes to completion time
  const handleCompletionTimeChange = (index, value) => {
    const newCompletionTimes = [...formData.completion_times];
    newCompletionTimes[index] = value;
    
    const arrivalTime = getTimeValue(formData.arrival_times[0], formData.arrival_times[1]);
    const completionTime = getTimeValue(newCompletionTimes[0], newCompletionTimes[1]);
    
    if (completionTime - arrivalTime < 1.5) {
      const newArrivalValue = completionTime - 1.5;
      const newHour = Math.floor(newArrivalValue);
      const newMinutes = Math.round((newArrivalValue % 1) * 60);
      
      const formattedMinutes = minutesOptions.reduce((closest, current) => {
        return Math.abs(current - newMinutes) < Math.abs(closest - newMinutes) ? current : closest;
      }, minutesOptions[0]);
      
      const updatedFormData = {
        ...formData,
        completion_times: newCompletionTimes,
        arrival_times: [
          newHour.toString().padStart(2, '0'),
          formattedMinutes
        ]
      };
      
      setFormData(updatedFormData);
      validateFormData(updatedFormData);
    } else {
      const updatedFormData = {
        ...formData,
        completion_times: newCompletionTimes
      };
      
      setFormData(updatedFormData);
      validateFormData(updatedFormData);
    }
  };

  // Handle date change
  const handleDateChange = (e) => {
    const updatedFormData = { 
      ...formData, 
      date: e.target.value 
    };
    
    setFormData(updatedFormData);
    validateFormData(updatedFormData);
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      
      const isValid = validateFormData(formData);
      if (!isValid) {
        notification.error({ 
          message: "Validation Failed", 
          description: "Please fix the errors before saving."
        });
        setIsLoading(false);
        return;
      }
      
      if (propertyId) {
        const dataToSend = new FormData();
        dataToSend.append("propertyid", propertyId);
        dataToSend.append("type", 1);
        
        formData.arrival_times.forEach(time => {
          const paddedTime = time.toString().padStart(2, '0');
          dataToSend.append("arrival_times[]", paddedTime);
        });
        
        formData.completion_times.forEach(time => {
          const paddedTime = time.toString().padStart(2, '0');
          dataToSend.append("completion_times[]", paddedTime);
        });
        
        dataToSend.append("date", formData.date);
        
        if (orderId) {
          dataToSend.append("order_id", orderId);
        }

        const response = await propertiesService.updatePropertyDetails(dataToSend);
        
        if (response.status) {
          const arrivalTime = `${formData.arrival_times[0].padStart(2, '0')}:${formData.arrival_times[1].padStart(2, '0')}`;
          const completionTime = `${formData.completion_times[0].padStart(2, '0')}:${formData.completion_times[1].padStart(2, '0')}`;
          
          const schedule = {
            arrival_time: arrivalTime,
            completion_time: completionTime,
            order_date: formData.date
          };
          
          const dateObj = new Date(formData.date);
          const displayDate = dateObj.toLocaleDateString('en-GB', {
            day: 'numeric', 
            month: 'short', 
            year: 'numeric'
          });
          
          // ✅ KEY FIX: Save to localStorage immediately
          try {
            const savedSchedules = JSON.parse(localStorage.getItem('dateIsSet') || '{}');
            savedSchedules[propertyId] = schedule;
            localStorage.setItem('dateIsSet', JSON.stringify(savedSchedules));
            
            // Emit events for UI updates
            eventBus.emit('schedule:updated', savedSchedules);
            eventBus.emit('datetime:updated', {
              propertyId, 
              displayDate,
              timeSlot: `${arrivalTime} - ${completionTime}`
            });
          } catch (e) {
            console.error("Error saving schedule to localStorage", e);
          }
          
          await refreshCart();
          
          notification.success({ 
            message: "Schedule Set Successfully",
            description: "Date and time have been set for this property."
          });
          
          if (onSave) {
            onSave(schedule);
          }
          
          handleModalClose(true);
        } else {
          throw new Error(response.message || "Failed to update schedule");
        }
      } else {
        // ✅ KEY FIX: For new properties, still save to localStorage
        const arrivalTime = `${formData.arrival_times[0].padStart(2, '0')}:${formData.arrival_times[1].padStart(2, '0')}`;
        const completionTime = `${formData.completion_times[0].padStart(2, '0')}:${formData.completion_times[1].padStart(2, '0')}`;
        
        const schedule = {
          arrival_time: arrivalTime,
          completion_time: completionTime,
          order_date: formData.date
        };
        
        if (propertyId) {
          try {
            const savedSchedules = JSON.parse(localStorage.getItem('dateIsSet') || '{}');
            savedSchedules[propertyId] = schedule;
            localStorage.setItem('dateIsSet', JSON.stringify(savedSchedules));
            
            const dateObj = new Date(formData.date);
            const displayDate = dateObj.toLocaleDateString('en-GB', {
              day: 'numeric', 
              month: 'short', 
              year: 'numeric'
            });
            
            eventBus.emit('schedule:updated', savedSchedules);
            eventBus.emit('datetime:updated', {
              propertyId, 
              displayDate,
              timeSlot: `${arrivalTime} - ${completionTime}`
            });
          } catch (e) {
            console.error("Error saving schedule", e);
          }
        }
        
        if (onSave) {
          onSave(schedule);
        }
        
        handleModalClose(true);
      }
    } catch (error) {
      console.error("Error updating dates:", error);
      notification.error({ 
        message: "Failed to update schedule",
        description: error.message || "An unknown error occurred"
      });
      setIsLoading(false);
    }
  };

  const refreshCart = async () => {
    try {
      const response = await cartService.getCart();
      if (response && response.cart) {
        eventBus.emit('cart:updated', response.cart);
      }
    } catch (error) {
      console.error("Error refreshing cart:", error);
    }
  };

  const handleModalClose = (saveData = false) => {
    setIsLoading(false);
    
    if (onClose) {
      onClose();
    }
  };

  const hasErrors = validationErrors.date || validationErrors.time;

  return (
    <Dialog open={isOpen} onClose={() => handleModalClose(false)}>
      <div className="fixed inset-0 bg-black/50 z-50" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-lg max-w-xl w-full shadow-xl">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <Dialog.Title className="text-2xl font-bold text-navy-800">
                Set Date & Time
              </Dialog.Title>
              <button 
                onClick={() => handleModalClose(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div>
              <h3 className="text-xl font-medium mb-6">Schedule Details</h3>
              
              {/* Date input */}
              <div className="mb-6">
                <label className="block mb-2 text-sm font-medium">Date *</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={handleDateChange}
                  min={userRoleUtils.isAdminOrImpersonating() ? null : getMinimumDate()}
                  className={`w-full p-3 border ${validationErrors.date ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-lg`}
                />
                
                {validationErrors.date && (
                  <div className="mt-2 text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    <span className="text-sm">{validationErrors.date}</span>
                  </div>
                )}
                
                {userRoleUtils.isAdminOrImpersonating() && (
                  <p className="mt-2 text-xs text-amber-600 flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Admin mode: You can select any date including past dates.
                  </p>
                )}
              </div>
              
              {/* Arrival time */}
              <div className="mb-6">
                <label className="block mb-2 text-sm font-medium">
                  Earliest Arrival Time *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <select
                    value={formData.arrival_times[0]}
                    onChange={(e) => handleArrivalTimeChange(0, e.target.value)}
                    className={`w-full p-3 border ${validationErrors.time ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-lg text-center`}
                  >
                    {hoursOptions.map(hour => (
                      <option key={hour} value={hour}>{hour}</option>
                    ))}
                  </select>
                  <select
                    value={formData.arrival_times[1]}
                    onChange={(e) => handleArrivalTimeChange(1, e.target.value)}
                    className={`w-full p-3 border ${validationErrors.time ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-lg text-center`}
                  >
                    {minutesOptions.map(min => (
                      <option key={min} value={min}>{min}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Completion time */}
              <div className="mb-6">
                <label className="block mb-2 text-sm font-medium">
                  Latest Completion Time * <span className="text-xs text-gray-500">(minimum 1.5 hours after arrival)</span>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <select
                    value={formData.completion_times[0]}
                    onChange={(e) => handleCompletionTimeChange(0, e.target.value)}
                    className={`w-full p-3 border ${validationErrors.time ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-lg text-center`}
                  >
                    {hoursOptions.map(hour => (
                      <option key={hour} value={hour}>{hour}</option>
                    ))}
                  </select>
                  <select
                    value={formData.completion_times[1]}
                    onChange={(e) => handleCompletionTimeChange(1, e.target.value)}
                    className={`w-full p-3 border ${validationErrors.time ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-lg text-center`}
                  >
                    {minutesOptions.map(min => (
                      <option key={min} value={min}>{min}</option>
                    ))}
                  </select>
                </div>
                
                {validationErrors.time && (
                  <div className="mt-2 text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    <span className="text-sm">{validationErrors.time}</span>
                  </div>
                )}
                
                {!validationErrors.time && (
                  <div className="mt-2 text-green-600 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    <span className="text-sm">Time slot: {formData.arrival_times[0]}:{formData.arrival_times[1]} - {formData.completion_times[0]}:{formData.completion_times[1]}</span>
                  </div>
                )}
              </div>
              
              {/* Warning message for short time slots */}
              {getTimeValue(formData.completion_times[0], formData.completion_times[1]) - getTimeValue(formData.arrival_times[0], formData.arrival_times[1]) < 2 && 
               getTimeValue(formData.completion_times[0], formData.completion_times[1]) - getTimeValue(formData.arrival_times[0], formData.arrival_times[1]) >= 1.5 && (
                <div className="mb-6">
                  <Alert
                    message="Short Time Slot"
                    description="The selected time slot is less than 2 hours but meets the minimum requirement of 1.5 hours."
                    type="info"
                    showIcon
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-4 mt-8">
              <button
                onClick={() => handleModalClose(false)}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                hidden={isSaveHidden}
                disabled={hasErrors || isLoading}
                className={`px-6 py-2 ${hasErrors || isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-golden-navy hover:bg-opacity-90'} text-white rounded-lg font-medium flex items-center`}
              >
                {isLoading ? (
                  <>
                    <Clock className="animate-spin h-4 w-4 mr-2" />
                    Saving...
                  </>
                ) : (
                  'Set Date & Time'
                )}
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default DateTimeModal;